/* eslint-disable react/static-property-placement */
import { FC, useEffect } from 'react';
import { BrowserRouter, Navigate, Routes, Route } from 'react-router-dom';

import Config from 'Config';
import Location from 'services/Location';
import { buildOauthQuery } from 'helpers/oath-query-builder';
import { Chooser } from 'routes/chooser/Chooser';
import { Signin } from 'routes/signin/Signin';
import { CustomSSO } from 'routes/custom-sso/CustomSSO';
import { Signup } from 'routes/signup/Signup';
import { LoginButton } from 'routes/login-button/LoginButton';
import { ForgotPassword } from 'routes/forgot-password/ForgotPassword';
import { SetPassword } from 'routes/set-password/SetPassword';
import { ActivateAccount } from 'routes/activate-account/ActivateAccount';
import { Consent } from 'routes/consent/Consent';
import { Ooops } from 'routes/ooops/Ooops';
import { Join } from 'routes/join/Join';
import { SetEmail } from 'routes/set-email/SetEmail';
import { Wizard } from 'routes/wizard/Wizard';
import { Verification } from 'routes/verification/Verification';
import { ActivateProduct } from 'routes/activate-product/ActivateProduct';
import { StopScreen } from 'routes/stop-screen/StopScreen';
import { Logout } from 'routes/logout-screen/Logout';
import { SelectTrial } from 'routes/select-trial/SelectTrial';

const ROUTES = [
  '', // first route is default
  'custom-sso',
  'signup',
  'select-trial',
  'forgot-password',
  'set-password',
  'activate-product',
  'activate',
  'logout',
  'consent',
  'ooops',
  'login-button/success',
  'login-button/error',
  'join',
  'set-email',
  'choose-organization',
  'wizard',
  'verification',
  'stop-screen',
];

const ROUTE_REDIRECTS = {
  signin: '/',
  'signup/credentials': '/signup',
  'signup/website': '/signup',
  'signup/company': '/signup',
  'reset-password': '/set-password',
  'wrong-sso': '/?identity_exception=wrong_sso',
};

type Props = {
  isTokenValid: boolean;
};

export const Router: FC<Props> = ({ isTokenValid }) => {
  const getRouteFromUrl = (): string => {
    const currentUrl = Location.getPathName();

    return ROUTES.find((route, index) => {
      if (index === 0) {
        return route === currentUrl;
      }

      return new RegExp(`^${route}`).test(currentUrl);
    });
  };

  const getDefaultRedirectRoute = (): string => ROUTES[0];

  useEffect(() => {
    window.onpopstate = () => {
      followRedirects();
    };
    followRedirects();
  }, []);

  const getRequireLoginRoutes = (): string[] => {
    const requireLoginRoutes = ['activate-product', 'choose-organization', 'set-email', 'wizard'];

    return requireLoginRoutes;
  };

  const isRouteDefault = (): boolean => Location.getPathName() === getDefaultRedirectRoute();

  const followRedirects = (): void => {
    const url = Location.getPathName();

    if (ROUTE_REDIRECTS[url]) {
      const redirectUrl = `${window.location.origin}${ROUTE_REDIRECTS[url]}${window.location.search}`;
      window.location.href = redirectUrl;
    }

    if (
      (!getRouteFromUrl() && isRouteDefault()) ||
      (!isTokenValid && getRequireLoginRoutes().includes(url))
    ) {
      let param;

      if (
        !Location.getUrlParam('client_id') &&
        !Location.getUrlParam('response_type') &&
        !Location.getUrlParam('redirect_uri')
      ) {
        param = {
          client_id: Config.client_id,
          redirect_uri:
            encodeURIComponent(Location.getPathName()) ||
            encodeURIComponent(import.meta.env.VITE_APP_DEFAULT_REDIRECT_URI),
          response_type: Config.response_type,
        };
      }

      const queryParams = param ? `?${buildOauthQuery(param)}` : window.location.search;
      const redirectRoute = `/${getDefaultRedirectRoute()}${queryParams}`;
      window.history.replaceState(null, '', redirectRoute);
    }
  };

  return (
    <BrowserRouter>
      <Routes>
        <Route path="login-button/success" element={<LoginButton />} />
        <Route path="" element={<Signin />} />
        <Route path="choose-organization" element={<Chooser />} />
        <Route path="custom-sso/*" element={<CustomSSO />} />
        <Route path="signup" element={<Signup />} />
        <Route path="select-trial" element={<SelectTrial />} />
        <Route path="forgot-password" element={<ForgotPassword />} />
        <Route path="set-password/*" element={<SetPassword />} />
        <Route path="activate/*" element={<ActivateAccount />} />
        <Route path="consent" element={<Consent />} />
        <Route
          path="ooops"
          element={
            <Ooops
              flow={Location.getUrlParam('flow')}
              identityException={Location.getUrlParam('identity_exception')}
              oauthException={Location.getUrlParam('oauth_exception')}
              exceptionDetails={Location.getUrlParam('exception_details')}
              requestId={Location.getUrlParam('request_id')}
              traceId={Location.getUrlParam('trace_id')}
            />
          }
        />
        <Route path="join" element={<Join />} />
        <Route path="set-email" element={<SetEmail />} />
        <Route path="wizard" element={<Wizard />} />
        <Route path="verification" element={<Verification />} />
        <Route path="activate-product/*" element={<ActivateProduct />} />
        <Route path="stop-screen" element={<StopScreen />} />
        <Route path="logout" element={<Logout />} />
        <Route
          path="*"
          element={<Navigate to={`${ROUTE_REDIRECTS.signin}${window.location.search}`} replace />}
        />
      </Routes>
    </BrowserRouter>
  );
};
